import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { combineAll, UuidService } from '@datachain/ui-sdk/common';
import { AppModalService } from '@datachain/ui-sdk/components/app-modal';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
	catchError,
	combineLatest,
	concatMap,
	exhaustMap,
	filter,
	from,
	map,
	of,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs';

import { NgCommService } from '../../ajs-ng-communication.service';
import { AjsScopeService } from '../../ajs-scope.service';
import { AjsStateService } from '../../ajs-state.service';
import { states } from '../../core/application.states';
import { DcCoreElementType } from '../../core/dc-core-types';
import { AppSelector } from '../../core/store';
import { isCurrentPage } from '../../core/utils/is-current-page';
import { PermissionRequester } from '../../requesters';
import { SubscriptionRequester } from '../../requesters/subscription/subscription.requester';
import {
	ColumnModelAction,
	ColumnModelActionConfirmPopupComponent,
	IColumnModelConfirmationModalData,
} from '../components/column-models/column-model-action-confirm-popup/column-model-action-confirm-popup.component';
import {
	FilterModelAction,
	FilterModelActionConfirmPopupComponent,
	IFilterModelConfirmationModalData,
} from '../components/filter-models/filter-model-action-confirm-popup/filter-model-action-confirm-popup.component';
import {
	IProfileActionConfirmationModalData,
	ProfileActionConfirmPopupComponent,
} from '../components/profiles/profile-action-confirm-popup/profile-action-confirm-popup.component';
import {
	ISubscriptionConfirmationModalData,
	SubscriptionActionConfirmPopupComponent,
	SubscriptionActionEnum,
} from '../components/subscription-action-confirm-popup/subscription-action-confirm-popup.component';
import { InternalMetadataPopupComponent } from '../components/subscription-view/internal-metadata-popup/internal-metadata-popup.component';
import { PublicMetadataPopupComponent } from '../components/subscription-view/public-metadata-popup/public-metadata-popup.component';
import { InternalMetadataEntity } from '../domain/internal-metadata.entity';
import { PublicMetadataEntity } from '../domain/public-metadata.entity';
import { ProjectSubscriptionAdapter } from '../project-subscription.adapter';
import { RuleMapperService } from '../subscription-filter.adapter';
import {
	activateBulkSubscription,
	activateBulkSubscriptionSuccess,
	activateSubscription,
	activateSubscriptionFail,
	activateSubscriptionSuccess,
	clearBulkSubscriptionData,
	clearBulkSubscriptionDataSuccess,
	clearSubscriptionData,
	clearSubscriptionDataFail,
	clearSubscriptionDataSuccess,
	closeNewSubscriptionModal,
	createNewColumnModel,
	createNewColumnModelFail,
	createNewColumnModelSuccess,
	createNewFilterModel,
	createNewFilterModelFail,
	createNewFilterModelSuccess,
	createNewProfile,
	createNewProfileFail,
	createNewProfileSuccess,
	deactivateBulkSubscription,
	deactivateBulkSubscriptionSuccess,
	deactivateSubscription,
	deactivateSubscriptionFail,
	deactivateSubscriptionSuccess,
	deleteBulkColumnModel,
	deleteBulkConsumedProject,
	deleteBulkFilterModel,
	deleteBulkProfile,
	deleteBulkSubscription,
	deleteColumnModel,
	deleteColumnModelFail,
	deleteColumnModelSuccess,
	deleteConsumedProject,
	deleteConsumedProjectFail,
	deleteConsumedProjectSuccess,
	deleteFilterModel,
	deleteFilterModelFail,
	deleteFilterModelSuccess,
	deleteProfile,
	deleteProfileFail,
	deleteProfileSuccess,
	deleteSubscription,
	deleteSubscriptionFail,
	deleteSubscriptionSuccess,
	fetchConsumedProfileColumnsFail,
	fetchConsumedProfileColumnsSuccess,
	fetchConsumedProjectsFail,
	fetchConsumedProjectsSuccess,
	fetchMembersAccessFail,
	fetchMembersAccessSuccess,
	fetchProjectsFail,
	fetchProjectsSuccess,
	fetchPublicationHistoriesFail,
	fetchPublicationHistoriesSuccess,
	fetchSubscriptionColumnModelsFail,
	fetchSubscriptionColumnModelsSuccess,
	fetchSubscriptionColumnsFail,
	fetchSubscriptionColumnsSuccess,
	fetchSubscriptionFail,
	fetchSubscriptionFilterModelsFail,
	fetchSubscriptionFilterModelsSuccess,
	fetchSubscriptionProfilesFail,
	fetchSubscriptionProfilesSuccess,
	fetchSubscriptionsFail,
	fetchSubscriptionsSuccess,
	fetchSubscriptionSuccess,
	fetchSwitchableConsumedProjectsFail,
	fetchSwitchableConsumedProjectsSuccess,
	getConsumedProfileColumns,
	getProjects,
	getPublicationHistories,
	getSubscriptions,
	getSwitchableConsumedProjects,
	getSwitchableConsumedProjectsForSubscription,
	hasSubscriptionCreatePermissionFail,
	hasSubscriptionCreatePermissionSuccess,
	initializeSubscription,
	initializeSubscriptionFail,
	initializeSubscriptionOnList,
	initializeSubscriptionSuccess,
	initPermissionsConfig,
	initSubscriptions,
	initSubscriptionView,
	navigateToSubscriptionCreate,
	navigateToSubscriptionCreateFromDatablock,
	navigateToSubscriptionDetails,
	navigateToSubscriptionList,
	openDeleteColumnModelConfirmPopup,
	openDeleteConfirmPopupOnView,
	openDeleteFilterModelConfirmPopup,
	openDeleteProfileConfirmPopup,
	openInitializeConfirmPopup,
	openUpdateInternalMetadataPopup,
	openUpdatePublicMetadataPopup,
	refreshConsumedProjects,
	refreshMembersAccess,
	refreshProjects,
	refreshPublicationHistories,
	refreshPublishedSubscription,
	refreshPublishedSubscriptionFail,
	refreshPublishedSubscriptionSuccess,
	refreshSubscriptionColumnModels,
	refreshSubscriptionColumns,
	refreshSubscriptionFilterModels,
	refreshSubscriptionProfiles,
	refreshSubscriptionProfilesWithModels,
	refreshSubscriptions,
	refreshSubscriptionsSuccess,
	refreshSubscriptionView,
	refreshSwitchableConsumedProjects,
	refreshSwitchableConsumedProjectsForSubscription,
	reloadProfilesAfterAction,
	reloadProfilesAfterActionSuccess,
	saveNewConsumers,
	saveNewConsumersFail,
	saveNewConsumersSuccess,
	SubscriptionActions,
	switchVersionForConsumedProjects,
	switchVersionForConsumedProjectsFail,
	switchVersionForConsumedProjectsOnView,
	switchVersionForConsumedProjectsOnViewFail,
	switchVersionForConsumedProjectsOnViewSuccess,
	switchVersionForConsumedProjectsSuccess,
	toggleBulkProjectConsume,
	toggleProjectConsume,
	toggleProjectConsumeFail,
	toggleProjectConsumeSuccess,
	updateColumnModel,
	updateColumnModelFail,
	updateColumnModelSuccess,
	updateConsumer,
	updateConsumerFail,
	updateConsumerSuccess,
	updateFilterModel,
	updateFilterModelFail,
	updateFilterModelSuccess,
	updateInternalMetadata,
	updateInternalMetadataFail,
	updateInternalMetadataSuccess,
	updateMembersAccess,
	updateMembersAccessFail,
	updateMembersAccessSuccess,
	updateProfile,
	updateProfileFail,
	updateProfileSuccess,
	updatePublicMetadata,
	updatePublicMetadataFail,
	updatePublicMetadataSuccess,
	updateSubscriptionActionInProgress,
	updateSubscriptionColumns,
	updateSubscriptionColumnsFail,
	updateSubscriptionColumnsSuccess,
	updateSubscriptionData,
	updateSubscriptionDataFail,
	updateSubscriptionDataOnList,
	updateSubscriptionDataSuccess,
} from './subscription.actions';
import { SubscriptionSelector } from './subscription.selector';

interface ISubscriptionJobSocketResponse {
	type: string;
	data: {
		id: number;
		client_id: string;
		element_id: number;
		element_type: string;
		job_id: string;
		status: string;
	};
}

@Injectable()
export class SubscriptionEffects {
	// ##########################################################
	// Navigation Effects
	// ##########################################################
	public navigateToSubscriptionList$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToSubscriptionList),
				tap(() => this.stateService.go(states.SubscriptionList))
			),
		{
			dispatch: false,
		}
	);

	public navigateToSubscriptionDetails$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToSubscriptionDetails),
				tap((action) =>
					this.stateService.go(states.SubscriptionView, {
						subscriptionId: `${action.subscriptionId}`,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateToSubscriptionCreate$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToSubscriptionCreate),
				tap(() => this.stateService.go(states.SubscriptionCreate))
			),
		{
			dispatch: false,
		}
	);

	public navigateToSubscriptionCreateFromDatablock$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(navigateToSubscriptionCreateFromDatablock),
				tap((action) =>
					this.stateService.go(states.SubscriptionCreateFromDatablockView, {
						datablockId: `${action.datablockId}`,
						datablockLabel: action.datablockLabel,
					})
				)
			),
		{
			dispatch: false,
		}
	);

	public navigateAfterCloseNewSubscriptionModal$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(closeNewSubscriptionModal),
				tap((action) => {
					this.modalService.closeActiveModal();
					if (isCurrentPage('datablocks', 'edit')) {
						this.stateService.go(states.DatablockEdit, {
							id: `${action.datablockId}`,
						});
						return;
					}

					this.stateService.go(states.SubscriptionList);
				})
			),
		{
			dispatch: false,
		}
	);

	// ##########################################################
	// Business Effects
	// ##########################################################
	public getSubscriptions$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getSubscriptions),
			switchMap((action) =>
				this.requester.getSubscriptions(
					action.size,
					action.page,
					action.search,
					action.filteredFields,
					action.sortedFields,
					false,
					true
				)
			),
			map((response) =>
				fetchSubscriptionsSuccess({
					subscriptions: this.subscriptionAdapter.generateSubscriptions(
						response.elements
					),
					total: response.total,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSubscriptionsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshSubscriptionAfterWS$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshSubscriptions),
			concatMap((action) =>
				this.requester.getSubscription(action.subscriptionId, false)
			),
			withLatestFrom(this.selector.getSubscriptions$()),
			map(([updatedSubscription, list]) =>
				refreshSubscriptionsSuccess({
					subscriptions: this.subscriptionAdapter.generateSubscriptionsAfterWS(
						list,
						updatedSubscription
					),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSubscriptionsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getCreatePermission$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initSubscriptions),
			switchMap(() =>
				this.permissionRequester.haveCreatePermission(
					DcCoreElementType.DATALINK
				)
			),
			map((canCreate) =>
				hasSubscriptionCreatePermissionSuccess({
					canCreate,
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					hasSubscriptionCreatePermissionFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getSubscription$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initSubscriptionView, refreshSubscriptionView),
			switchMap((action) =>
				combineAll({
					subscription: this.requester.getSubscription(action.subscriptionId),
					columns: this.requester.getSubscriptionColumns(action.subscriptionId),
					columnModels: this.requester.getSubscriptionColumnModels(
						action.subscriptionId
					),
					filterModels: this.requester.getSubscriptionFilterModels(
						action.subscriptionId
					),
					consumedProjects: this.requester.getConsumedProjects(
						action.subscriptionId
					),
					permissions:
						this.permissionRequester.getCurrentUserElementPermissions(
							DcCoreElementType.DATALINK,
							action.subscriptionId
						),
				})
			),
			map(
				({
					subscription,
					columns,
					columnModels,
					filterModels,
					consumedProjects,
					permissions,
				}) =>
					fetchSubscriptionSuccess({
						subscription:
							this.subscriptionAdapter.generateSubscriptionDetailsEntity(
								subscription,
								columns,
								columnModels,
								filterModels,
								consumedProjects,
								permissions
							),
					})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSubscriptionFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getRefreshSubscriptionSync$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshPublishedSubscription),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([, subscriptionId]) =>
				this.requester.getSubscription(subscriptionId)
			),
			withLatestFrom(this.selector.getPublishedSubscription$()),
			map(([result, currentPublishedSubscription]) =>
				refreshPublishedSubscriptionSuccess({
					publishedSubscription:
						this.subscriptionAdapter.getPublishedSubscription(
							currentPublishedSubscription,
							result
						),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					refreshPublishedSubscriptionFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deleteSubscription$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteSubscription),
			switchMap((action) =>
				this.requester.deleteSubscription(action.subscriptionId)
			),
			map(() => deleteSubscriptionSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.delete.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					deleteSubscriptionFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deleteBulkSubscription$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteBulkSubscription),
			switchMap((action) =>
				combineLatest(
					action.subscriptionIds.map((id) =>
						this.requester.deleteSubscription(id)
					)
				)
			),
			map(() => deleteSubscriptionSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.delete.multiple.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					deleteSubscriptionFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public createNewProfile$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createNewProfile),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.createSubscriptionProfile(
					subscriptionId,
					action.profile,
					action.columnModel,
					action.filterModels
				)
			),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.profile.create.success:`
				);
			}),
			map((result) => createNewProfileSuccess()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					createNewProfileFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateProfile$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateProfile),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.updateProfile(
					subscriptionId,
					action.profile,
					action.columnModel,
					action.filterModels
				)
			),
			map((result) => updateProfileSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.profile.update.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					updateProfileFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deleteProfile$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteProfile),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.deleteProfile(subscriptionId, action.profileId)
			),
			map(() => deleteProfileSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.profile.delete.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					deleteProfileFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public createNewColumnModel$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createNewColumnModel),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.createSubscriptionColumnModel(
					subscriptionId,
					action.columnModel
				)
			),
			map((result) => createNewColumnModelSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.columnModel.create.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					createNewColumnModelFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateColumnModel$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateColumnModel),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.updateColumnModel(subscriptionId, action.columnModel)
			),
			map((result) => updateColumnModelSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.columnModel.update.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					updateColumnModelFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deleteColumnModel$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteColumnModel),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.deleteColumnModel(subscriptionId, action.columnModelId)
			),
			map(() => deleteColumnModelSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.columnModel.delete.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					deleteColumnModelFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deleteFilterModel$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteFilterModel),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.deleteFilterModel(subscriptionId, action.filterModelId)
			),
			map(() => deleteFilterModelSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.filterModel.delete.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					deleteFilterModelFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateSubscriptionColumns$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateSubscriptionColumns),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				combineAll({
					subscriptionId,
					result: this.requester.updateSubscriptionColumns(
						subscriptionId,
						action.columns
					),
				})
			),
			map(({ subscriptionId }) =>
				updateSubscriptionColumnsSuccess({
					subscriptionId,
				})
			),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.columns.update.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@subscription.error.update.columns:`
				);
				this.store.dispatch(
					updateSubscriptionColumnsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateInternalMetadata$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateInternalMetadata),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.updateInternalMetadata(
					subscriptionId,
					action.internalMetadata
				)
			),
			map((response) =>
				updateInternalMetadataSuccess({
					internalMetadata:
						this.subscriptionAdapter.generateInternalMetadataEntity(response),
				})
			),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.internalMetadata.update.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					updateInternalMetadataFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updatePublicMetadata$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updatePublicMetadata),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.updatePublicMetadata(
					subscriptionId,
					action.publicMetadata
				)
			),
			map((response) =>
				updatePublicMetadataSuccess({
					publicMetadata:
						this.subscriptionAdapter.generatePublicMetadataEntity(response),
				})
			),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.publicMetadata.update.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					updatePublicMetadataFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshSubscriptionProfiles$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshSubscriptionProfiles),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([, subscriptionId]) =>
				this.requester.getSubscriptionProfiles(subscriptionId, false)
			),
			withLatestFrom(this.selector.getSubscriptionColumns$()),
			map(([response, subscriptionColumns]) => {
				const generatedProfiles =
					this.subscriptionAdapter.generateSubscriptionProfiles(
						response,
						subscriptionColumns
					);
				return fetchSubscriptionProfilesSuccess({
					profiles: generatedProfiles,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSubscriptionProfilesFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public reloadProfilesAfterProfileAction$ = createEffect(() =>
		this.actions$.pipe(
			ofType(reloadProfilesAfterAction),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([, subscriptionId]) =>
				this.requester.getSubscriptionProfiles(subscriptionId, false)
			),
			withLatestFrom(this.selector.getSubscriptionColumns$()),
			map(([response, subscriptionColumns]) => {
				const generatedProfiles =
					this.subscriptionAdapter.generateSubscriptionProfiles(
						response,
						subscriptionColumns
					);
				return reloadProfilesAfterActionSuccess({
					profiles: generatedProfiles,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSubscriptionProfilesFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshSubscriptionColumnModels$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshSubscriptionColumnModels),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([, subscriptionId]) =>
				this.requester.getSubscriptionColumnModels(subscriptionId, false)
			),
			withLatestFrom(
				this.selector.getSubscriptionColumns$(),
				this.selector.getSubscriptionProfiles$()
			),
			map(([response, subscriptionColumns, subscriptionProfiles]) => {
				const columnModels =
					this.subscriptionAdapter.generateSubscriptionColumnModels(
						response,
						subscriptionColumns,
						subscriptionProfiles
					);
				return fetchSubscriptionColumnModelsSuccess({
					columnModels,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSubscriptionColumnModelsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshSubscriptionFilterModels$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshSubscriptionFilterModels),
			withLatestFrom(this.selector.getSubscriptionId$()),
			exhaustMap(([, subscriptionId]) =>
				this.requester.getSubscriptionFilterModels(subscriptionId, false)
			),
			withLatestFrom(this.selector.getSubscriptionProfiles$()),
			map(([response, subscriptionProfiles]) => {
				const filterModels =
					this.subscriptionAdapter.generateSubscriptionFilterModels(
						response,
						subscriptionProfiles
					);
				return fetchSubscriptionFilterModelsSuccess({
					filterModels,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSubscriptionFilterModelsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshSubscriptionColumns$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshSubscriptionColumns),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([, subscriptionId]) =>
				this.requester.getSubscriptionColumns(subscriptionId, false)
			),
			map((response) => {
				const generatedColumns =
					this.subscriptionAdapter.generateSubscriptionColumns(response);
				return fetchSubscriptionColumnsSuccess({
					columns: generatedColumns,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSubscriptionColumnsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public refreshConsumedProjects$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshConsumedProjects),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.getConsumedProjects(subscriptionId, action.filterVersion)
			),
			map((response) =>
				fetchConsumedProjectsSuccess({
					consumedProjects:
						this.subscriptionAdapter.generateConsumedProjects(response),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchConsumedProjectsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getSwitchableConsumedProjectsForSubscription$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				getSwitchableConsumedProjectsForSubscription,
				refreshSwitchableConsumedProjectsForSubscription
			),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([, subscriptionId]) =>
				this.requester.getSwitchableConsumedProjectsForSubscription(
					subscriptionId
				)
			),
			map((response) =>
				fetchSwitchableConsumedProjectsSuccess({
					projects:
						this.subscriptionAdapter.generateSwitchableConsumedProjects(
							response
						),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSwitchableConsumedProjectsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getSwitchableConsumedProjects$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getSwitchableConsumedProjects, refreshSwitchableConsumedProjects),
			switchMap(() => this.requester.getSwitchableConsumedProjects()),
			map((response) =>
				fetchSwitchableConsumedProjectsSuccess({
					projects:
						this.subscriptionAdapter.generateSwitchableConsumedProjects(
							response
						),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSwitchableConsumedProjectsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public switchVersionForConsumedProjectsOnView$ = createEffect(() =>
		this.actions$.pipe(
			ofType(switchVersionForConsumedProjectsOnView),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.switchVersionForSubscriptionConsumedProjects(
					subscriptionId,
					action.projectIds
				)
			),
			map((response) => switchVersionForConsumedProjectsOnViewSuccess()),
			tap(() => {
				this.modalService.closeActiveModal();
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.versionSwitcher.action.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@subscription.versionSwitcher.action.error:`
				);
				this.store.dispatch(
					switchVersionForConsumedProjectsOnViewFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public switchVersionForConsumedProjects$ = createEffect(() =>
		this.actions$.pipe(
			ofType(switchVersionForConsumedProjects),
			switchMap((action) =>
				this.requester.switchVersionForConsumedProjects(action.projectIds)
			),
			map(() => switchVersionForConsumedProjectsSuccess()),
			tap(() => {
				this.modalService.closeActiveModal();
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.versionSwitcher.action.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@subscription.versionSwitcher.action.error:`
				);
				this.store.dispatch(
					switchVersionForConsumedProjectsFail({
						error,
					})
				);
				return caught;
			})
		)
	);
	// public refreshSubscriptionViewAfterConsumersRefreshed$ = createEffect(() =>
	// 	this.actions$.pipe(
	// 		ofType(fetchConsumedProjectsSuccess),
	// 		withLatestFrom(this.selector.getSubscriptionId$()),
	// 		map(([, id]) =>
	// 			refreshSubscriptionView({
	// 				subscriptionId: id,
	// 			})
	// 		),
	// 		catchError((error: HttpErrorResponse, caught) => {
	// 			console.error(error);
	// 			return caught;
	// 		})
	// 	)
	// );

	public refreshProfilesWithModels$ = createEffect(() =>
		this.actions$.pipe(
			ofType(refreshSubscriptionProfilesWithModels),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([, subscriptionId]) =>
				this.requester.getSubscriptionProfiles(subscriptionId, false)
			),
			withLatestFrom(this.selector.getSubscriptionColumns$()),
			map(([response, subscriptionColumns]) => {
				const generatedProfiles =
					this.subscriptionAdapter.generateSubscriptionProfiles(
						response,
						subscriptionColumns
					);
				return fetchSubscriptionProfilesSuccess({
					profiles: generatedProfiles,
				});
			}),
			tap(() => {
				this.store.dispatch(refreshSubscriptionColumnModels());
				this.store.dispatch(refreshSubscriptionFilterModels());
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchSubscriptionProfilesFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public initializeSubscription$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initializeSubscription, initializeSubscriptionOnList),
			switchMap((action) => {
				const clientUuid = `${
					action.operationType
				}-${this.uuidService.uuidV4()}`;
				return this.requester.initializeSubscription(
					action.subscriptionId,
					clientUuid
				);
			}),
			map(() => initializeSubscriptionSuccess()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					initializeSubscriptionFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateSubscriptionData$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateSubscriptionData, updateSubscriptionDataOnList),
			switchMap((action) => {
				const clientUuid = `${
					action.operationType
				}-${this.uuidService.uuidV4()}`;

				return this.requester.updateSubscriptionData(
					action.subscriptionId,
					clientUuid
				);
			}),
			map(() => updateSubscriptionDataSuccess()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					updateSubscriptionDataFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public clearSubscriptionData$ = createEffect(() =>
		this.actions$.pipe(
			ofType(clearSubscriptionData),
			switchMap((action) =>
				combineAll({
					action,
					response: this.requester.clearSubscriptionData(action.subscriptionId),
				})
			),
			map(({ action }) =>
				clearSubscriptionDataSuccess({
					subscriptionId: action.subscriptionId,
					inViewMode: action.inViewMode,
				})
			),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.publication.action.clearData.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@subscription.publication.action.clearData.error:`
				);
				this.store.dispatch(
					clearSubscriptionDataFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public clearBulkSubscriptionData$ = createEffect(() =>
		this.actions$.pipe(
			ofType(clearBulkSubscriptionData),
			switchMap((action) =>
				combineLatest(
					action.subscriptionIds.map((id) =>
						this.requester.clearSubscriptionData(id)
					)
				)
			),
			map(() => clearBulkSubscriptionDataSuccess()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					clearSubscriptionDataFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deactivateSubscription$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deactivateSubscription),
			switchMap((action) =>
				combineAll({
					action,
					response: this.requester.deactivateSubscription(
						action.subscriptionId
					),
				})
			),
			map(({ action }) =>
				deactivateSubscriptionSuccess({
					subscriptionId: action.subscriptionId,
					inViewMode: action.inViewMode,
				})
			),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.publication.action.deactivate.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@subscription.publication.action.deactivate.error:`
				);
				this.store.dispatch(
					deactivateSubscriptionFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deactivateBulkSubscription$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deactivateBulkSubscription),
			switchMap((action) =>
				combineLatest(
					action.subscriptionIds.map((id) =>
						this.requester.deactivateSubscription(id)
					)
				)
			),
			map(() => deactivateBulkSubscriptionSuccess()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					deactivateSubscriptionFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public activateSubscription$ = createEffect(() =>
		this.actions$.pipe(
			ofType(activateSubscription),
			switchMap((action) =>
				combineAll({
					action,
					response: this.requester.activateSubscription(action.subscriptionId),
				})
			),
			map(({ action }) =>
				activateSubscriptionSuccess({
					subscriptionId: action.subscriptionId,
					inViewMode: action.inViewMode,
				})
			),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.publication.action.activate.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@subscription.publication.action.activate.error:`
				);
				this.store.dispatch(
					activateSubscriptionFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public activateBulkSubscription$ = createEffect(() =>
		this.actions$.pipe(
			ofType(activateBulkSubscription),
			switchMap((action) =>
				combineLatest(
					action.subscriptionIds.map((id) =>
						this.requester.activateSubscription(id)
					)
				)
			),
			map(() => activateBulkSubscriptionSuccess()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					activateSubscriptionFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deleteConsumedProject$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteConsumedProject),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.deleteConsumedProject(
					subscriptionId,
					action.consumedProjectId
				)
			),
			map(() => deleteConsumedProjectSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.consumedProject.delete.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					deleteConsumedProjectFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public deleteBulkConsumedProject$ = createEffect(() =>
		this.actions$.pipe(
			ofType(deleteBulkConsumedProject),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				combineAll({
					action,
					response: combineLatest(
						action.consumedProjectIds.map((id) =>
							this.requester.deleteConsumedProject(subscriptionId, id)
						)
					),
				})
			),
			map(() => deleteConsumedProjectSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.consumedProject.delete.multiple.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					deleteConsumedProjectFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getProjects$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getProjects, refreshProjects),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.getSubscribableProjects(
					subscriptionId,
					action.type !== SubscriptionActions.RefreshProjects
				)
			),
			map((result) =>
				fetchProjectsSuccess({
					projects: this.subscriptionAdapter.generateProjects(result),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchProjectsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public saveNewConsumers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(saveNewConsumers),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				combineLatest(
					action.consumerIds.map((id) =>
						this.requester.createNewConsumer(
							subscriptionId,
							action.profileUuid,
							+id
						)
					)
				)
			),
			map(() => saveNewConsumersSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.consumedProject.create.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					saveNewConsumersFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateConsumer$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateConsumer),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.updateConsumer(
					subscriptionId,
					action.profileUuid,
					action.projectUuid
				)
			),
			map(() => updateConsumerSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.consumedProject.update.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					updateConsumerFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public toggleProjectConsume$ = createEffect(() =>
		this.actions$.pipe(
			ofType(toggleProjectConsume),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				this.requester.updateProjectConsumeStatus(
					subscriptionId,
					action.id,
					action.isActive
				)
			),
			map(() => toggleProjectConsumeSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.consumer.status.update.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					toggleProjectConsumeFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public toggleBulkProjectConsume$ = createEffect(() =>
		this.actions$.pipe(
			ofType(toggleBulkProjectConsume),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) =>
				combineAll({
					action,
					response: combineLatest(
						action.ids.map((id) =>
							this.requester.updateProjectConsumeStatus(
								subscriptionId,
								id,
								action.isActive
							)
						)
					),
				})
			),
			map(() => toggleProjectConsumeSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.consumer.status.update.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					toggleProjectConsumeFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public listenToWsOnSubscription$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initSubscriptionView, initSubscriptions),
			switchMap((action) =>
				combineAll({
					action,
					stompResponse: from(
						this.ajsScopeService.listenOnRootScope<ISubscriptionJobSocketResponse>(
							'globalRunningJobs'
						)
					),
				})
			),
			filter(
				({ stompResponse }) => stompResponse.type === 'GenerateDataLinkSnapshot'
			),
			tap(({ action, stompResponse }) => {
				if (stompResponse?.data?.status.toLocaleLowerCase() === 'running') {
					this.store.dispatch(
						updateSubscriptionActionInProgress({
							subscriptionId: stompResponse.data.element_id,
							actionInProgress: 'in-progress',
						})
					);
				}
			}),
			filter(
				({ stompResponse }) =>
					stompResponse?.data?.status.toLocaleLowerCase() !== 'running' &&
					stompResponse?.data?.status.toLocaleLowerCase() !== 'waiting'
			),
			map(({ action, stompResponse }) => {
				if (stompResponse.data.status.toLocaleLowerCase() === 'error') {
					if (stompResponse.data.client_id.includes('initialize')) {
						this.ngCommService.notifyOnError(
							$localize`:i18n=@@subscription.initialization.action.error:`
						);
					} else if (stompResponse.data.client_id.includes('update-data')) {
						this.ngCommService.notifyOnError(
							$localize`:i18n=@@subscription.publication.update.action.error:`
						);
					} else {
						this.ngCommService.notifyOnError(
							$localize`:i18n=@@subscription.publication.action.error:`
						);
					}
				} else if (
					stompResponse.data !== undefined &&
					stompResponse?.data?.status.toLocaleLowerCase() !== 'error'
				) {
					if (stompResponse.data.client_id.includes('initialize')) {
						this.ngCommService.notifyOnSuccess(
							$localize`:i18n=@@subscription.initialization.action.success:`
						);
					} else if (stompResponse.data.client_id.includes('update-data')) {
						this.ngCommService.notifyOnSuccess(
							$localize`:i18n=@@subscription.publication.update.action.success:`
						);
					} else {
						this.ngCommService.notifyOnSuccess(
							$localize`:i18n=@@subscription.publication.action.success:`
						);
					}
				}

				if (
					isCurrentPage('project-subscription', 'view') &&
					action.type === SubscriptionActions.InitSubscriptionView
				) {
					return refreshSubscriptionView({
						subscriptionId: stompResponse.data.element_id,
					});
				}

				return refreshSubscriptions({
					subscriptionId: stompResponse.data.element_id,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public getPublicationHistories$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getPublicationHistories, refreshPublicationHistories),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([, subscriptionId]) =>
				this.requester.getAllHistories(subscriptionId)
			),
			map((result) =>
				fetchPublicationHistoriesSuccess({
					histories:
						this.subscriptionAdapter.generatePublicationHistories(result),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchPublicationHistoriesFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getMembersAccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(initPermissionsConfig, refreshMembersAccess),
			withLatestFrom(this.selector.getInternalMetadata$()),
			switchMap(([action, metadata]) =>
				this.requester.getSubscriptionPermission(
					metadata.id,
					action.type !== SubscriptionActions.RefreshMembersAccess
				)
			),
			map((result) =>
				fetchMembersAccessSuccess({
					membersAccess: this.subscriptionAdapter.generateMembersAccess(result),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchMembersAccessFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateMembersAccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateMembersAccess),
			withLatestFrom(
				this.selector.getInternalMetadata$(),
				this.appSelector.getAvailablePermissions$()
			),
			switchMap(([action, metadata, availablePermissions]) =>
				this.requester.updateSubscriptionMembersAccess(
					metadata.id,
					action.membersAccess,
					availablePermissions
				)
			),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.permissions.update.memberAccess.success:`
				);
			}),
			map(() => updateMembersAccessSuccess()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.ngCommService.notifyOnError(
					$localize`:i18n=@@subscription.permissions.update.memberAccess.error:`
				);
				this.store.dispatch(
					updateMembersAccessFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public getConsumedProfileColumns$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getConsumedProfileColumns),
			switchMap((action) =>
				combineAll({
					consumerId: action.consumerId,
					profileColumns: this.requester.getProjectProfileColumns(
						action.consumerId,
						true
					),
				})
			),
			map(({ consumerId, profileColumns }) =>
				fetchConsumedProfileColumnsSuccess({
					consumerId,
					profileColumns:
						this.subscriptionAdapter.generateProfileColumns(profileColumns),
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					fetchConsumedProfileColumnsFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public reloadSubscriptionViewAfterAction$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(updateSubscriptionColumnsSuccess),
				tap((action) => {
					this.store.dispatch(
						refreshSubscriptionView({
							subscriptionId: action.subscriptionId,
						})
					);
				})
			),
		{
			dispatch: false,
		}
	);

	public reloadSubscriptionViewOrListAfterAction$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				activateSubscriptionSuccess,
				deactivateSubscriptionSuccess,
				clearSubscriptionDataSuccess
			),
			map((action) => {
				if (action.inViewMode) {
					return refreshSubscriptionView({
						subscriptionId: action.subscriptionId,
					});
				}
				return refreshSubscriptions({
					subscriptionId: action.subscriptionId,
				});
			})
		)
	);

	public reloadSubscriptionProfilesAfterProfileAction$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					createNewProfileSuccess,
					updateProfileSuccess,
					deleteProfileSuccess
				),
				tap(() => {
					this.store.dispatch(refreshSubscriptionColumns());
					this.store.dispatch(reloadProfilesAfterAction());
					this.store.dispatch(refreshPublishedSubscription());
				})
			),
		{
			dispatch: false,
		}
	);

	public reloadSubscriptionModelsAfterReloadProfiles$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(reloadProfilesAfterActionSuccess),
				tap(() => {
					this.store.dispatch(refreshSubscriptionColumnModels());
					this.store.dispatch(refreshSubscriptionFilterModels());
				})
			),
		{
			dispatch: false,
		}
	);

	public reloadSubscriptionColumnModelAfterCreateAction$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(createNewColumnModelSuccess),
				tap(() => {
					this.store.dispatch(refreshSubscriptionColumnModels());
					this.store.dispatch(refreshSubscriptionColumns());
				})
			),
		{
			dispatch: false,
		}
	);

	public reloadSubscriptionProfilesModelsAfterColumnModelAction$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(updateColumnModelSuccess, deleteColumnModelSuccess),
				tap(() => {
					this.store.dispatch(refreshSubscriptionColumnModels());
					this.store.dispatch(refreshSubscriptionProfiles());
					this.store.dispatch(refreshSubscriptionColumns());
					this.store.dispatch(refreshPublishedSubscription());
				})
			),
		{
			dispatch: false,
		}
	);

	public reloadSubscriptionFilterModelAfterCreateAction$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(createNewFilterModelSuccess),
				tap(() => {
					this.store.dispatch(refreshSubscriptionFilterModels());
					this.store.dispatch(refreshSubscriptionColumns());
				})
			),
		{
			dispatch: false,
		}
	);

	public reloadSubscriptionProfilesFilterModelsAfterFilterModelAction$ =
		createEffect(
			() =>
				this.actions$.pipe(
					ofType(updateFilterModelSuccess, deleteFilterModelSuccess),
					tap(() => {
						this.store.dispatch(refreshSubscriptionFilterModels());
						this.store.dispatch(refreshSubscriptionColumns());
						this.store.dispatch(refreshSubscriptionProfiles());
						this.store.dispatch(refreshPublishedSubscription());
					})
				),
			{
				dispatch: false,
			}
		);

	// public reloadConsumedProjectsAfterAction$ = createEffect(
	// 	() =>
	// 		this.actions$.pipe(
	// 			ofType(
	// 				saveNewConsumersSuccess,
	// 				updateConsumerSuccess,
	// 				deleteConsumedProjectSuccess,
	// 				toggleProjectConsumeSuccess,
	// 				switchVersionForConsumedProjectsSuccess
	// 			),
	// 			tap(() => {
	// 				this.store.dispatch(
	// 					refreshConsumedProjects({
	// 						filterVersion: FilterVersion.AllVersions,
	// 					})
	// 				);
	// 			})
	// 		),
	// 	{
	// 		dispatch: false,
	// 	}
	// );

	public saveSubscriptionFilter$ = createEffect(() =>
		this.actions$.pipe(
			ofType(createNewFilterModel),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, currentSub]) =>
				this.requester.createSubscriptionFilterModel(currentSub, action.model)
			),
			map((result) => createNewFilterModelSuccess()),
			tap(() =>
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.filterModel.create.success:`
				)
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					createNewFilterModelFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	public updateFilterModel$ = createEffect(() =>
		this.actions$.pipe(
			ofType(updateFilterModel),
			withLatestFrom(this.selector.getSubscriptionId$()),
			switchMap(([action, subscriptionId]) => {
				const filterPayload = this.ruleMapperService.toBackendGroup(
					action.filterModel.root,
					new Map()
				);
				return this.requester.updateFilterModel(
					subscriptionId,
					action.filterModel,
					filterPayload
				);
			}),
			map((result) => updateFilterModelSuccess()),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.filterModel.update.success:`
				);
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				this.store.dispatch(
					updateFilterModelFail({
						error,
					})
				);
				return caught;
			})
		)
	);

	// ##########################################################
	// Confirmation popup effects
	// ##########################################################
	public openUpdateInternalMetadataPopup$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openUpdateInternalMetadataPopup),
			withLatestFrom(this.selector.getInternalMetadata$()),
			switchMap(([, metadata]) => {
				const modalRef = this.modalService.openPopup<{
					metadata: InternalMetadataEntity;
				}>(
					InternalMetadataPopupComponent,
					{
						title: $localize`:i18n=@@subscription.internalMetadata.update.popup.header:`,
						subTitle: '',
						width: '35%',
						metadata,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([modalRef.afterClosed()]);
			}),
			filter(([modalResult]) => modalResult !== undefined),
			map(([modalResult]) => {
				const { metadataConfig } = modalResult;
				return updateInternalMetadata({
					internalMetadata: metadataConfig,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public openUpdatePublicMetadataPopup$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openUpdatePublicMetadataPopup),
			withLatestFrom(this.selector.getPublicMetadata$()),
			switchMap(([, metadata]) => {
				const modalRef = this.modalService.openPopup<{
					metadata: PublicMetadataEntity;
				}>(
					PublicMetadataPopupComponent,
					{
						title: $localize`:i18n=@@subscription.publicMetadata.update.popup.header:`,
						subTitle: '',
						width: '35%',
						metadata,
					},
					{
						disableClose: true,
					}
				);
				return combineLatest([modalRef.afterClosed()]);
			}),
			filter(([modalResult]) => modalResult !== undefined),
			map(([modalResult]) => {
				const { metadataConfig } = modalResult;
				return updatePublicMetadata({
					publicMetadata: metadataConfig,
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public openDeleteProfileConfirmPopup$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openDeleteProfileConfirmPopup),
			switchMap((action) => {
				const modalRef =
					this.modalService.openPopup<IProfileActionConfirmationModalData>(
						ProfileActionConfirmPopupComponent,
						{
							title: $localize`:i18n=@@subscription.profile.action.confirmation.popup.delete.header:`,
							subTitle: '',
							id: action.profileId,
							element: action.profileLabel,
							action: 'DELETE_PROFILE',
						},
						{
							disableClose: true,
						}
					);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (!Array.isArray(action.profileId)) {
					return deleteProfile({
						profileId: action.profileId,
					});
				}
				return deleteBulkProfile({
					profileIds: [],
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public openDeleteColumnModelConfirmPopup$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openDeleteColumnModelConfirmPopup),
			switchMap((action) => {
				const modalRef =
					this.modalService.openPopup<IColumnModelConfirmationModalData>(
						ColumnModelActionConfirmPopupComponent,
						{
							title: $localize`:i18n=@@subscription.columnModel.action.confirm.popup.delete.header:`,
							subTitle: '',
							columnModelId: action.columnModelId,
							element: action.columnModelLabel,
							action: ColumnModelAction.Delete,
						},
						{
							disableClose: true,
						}
					);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (!Array.isArray(action.columnModelId)) {
					return deleteColumnModel({
						columnModelId: action.columnModelId,
					});
				}
				return deleteBulkColumnModel({
					columnModelIds: [],
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public openDeleteFilterModelConfirmPopup$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openDeleteFilterModelConfirmPopup),
			switchMap((action) => {
				const modalRef =
					this.modalService.openPopup<IFilterModelConfirmationModalData>(
						FilterModelActionConfirmPopupComponent,
						{
							title: $localize`:i18n=@@subscription.filterModel.action.confirm.popup.delete.header:`,
							subTitle: '',
							filterModelId: action.filterModelId,
							element: action.filterModelLabel,
							action: FilterModelAction.Delete,
						},
						{
							disableClose: true,
						}
					);
				return combineLatest([of(action), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([action]) => {
				if (!Array.isArray(action.filterModelId)) {
					return deleteFilterModel({
						filterModelId: action.filterModelId,
					});
				}
				return deleteBulkFilterModel({
					filterModelIds: [],
				});
			}),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public openDeleteConfirmPopupOnView$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openDeleteConfirmPopupOnView),
			withLatestFrom(
				this.selector.getSubscriptionId$(),
				this.selector.getInternalMetadata$()
			),
			switchMap(([, subscriptionId, internalMetadata]) => {
				const modalRef =
					this.modalService.openPopup<ISubscriptionConfirmationModalData>(
						SubscriptionActionConfirmPopupComponent,
						{
							title: $localize`:i18n=@@subscription.action.confirmation.popup.delete.header:`,
							subTitle: '',
							subscriptionId,
							element: internalMetadata.label,
							action: SubscriptionActionEnum.Delete,
						},
						{
							disableClose: true,
						}
					);
				return combineLatest([of(subscriptionId), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			switchMap(([subscriptionId]) =>
				this.requester.deleteSubscription(subscriptionId)
			),
			tap(() => {
				this.ngCommService.notifyOnSuccess(
					$localize`:i18n=@@subscription.delete.success:`
				);
			}),
			map(() => navigateToSubscriptionList()),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public openInitializeConfirmPopup$ = createEffect(() =>
		this.actions$.pipe(
			ofType(openInitializeConfirmPopup),
			withLatestFrom(
				this.selector.getSubscriptionId$(),
				this.selector.getInternalMetadata$()
			),
			switchMap(([, subscriptionId, internalMetadata]) => {
				const modalRef =
					this.modalService.openPopup<ISubscriptionConfirmationModalData>(
						SubscriptionActionConfirmPopupComponent,
						{
							title: $localize`:i18n=@@subscription.action.confirmation.popup.initialize.header:`,
							subTitle: '',
							subscriptionId,
							element: internalMetadata.label,
							action: SubscriptionActionEnum.Initialize,
						},
						{
							disableClose: true,
						}
					);
				return combineLatest([of(subscriptionId), modalRef.afterClosed()]);
			}),
			filter(([, modalResult]) => modalResult !== undefined),
			map(([subscriptionId]) =>
				initializeSubscription({
					subscriptionId,
					operationType: 'initialize',
				})
			),
			catchError((error: HttpErrorResponse, caught) => {
				console.error(error);
				return caught;
			})
		)
	);

	public constructor(
		private readonly actions$: Actions,
		private readonly store: Store,
		private readonly uuidService: UuidService,
		private readonly selector: SubscriptionSelector,
		private readonly appSelector: AppSelector,
		private readonly subscriptionAdapter: ProjectSubscriptionAdapter,
		private readonly requester: SubscriptionRequester,
		private readonly permissionRequester: PermissionRequester,
		private readonly modalService: AppModalService,
		private readonly stateService: AjsStateService,
		private readonly ngCommService: NgCommService,
		private readonly ajsScopeService: AjsScopeService,
		private readonly ruleMapperService: RuleMapperService
	) {}
}
